"use client";

import React from "react";
import { Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const COUNTRY_OPTIONS = ["United States", "Canada", "United Kingdom", "Germany", "France", "Singapore", "Australia", "Other"];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="border-border shadow-xl shadow-ring/5">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center space-y-4">
          <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <CircleCheck className="size-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Application Received</CardTitle>
          <p className="text-muted-foreground max-w-md">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="mt-6">
            Submit Another Inquiry
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="border-border shadow-xl shadow-ring/10 transition-all duration-500">
      <CardHeader className="text-center pb-8">
        <CardTitle className="text-3xl font-serif tracking-tight text-foreground">
          Institutional Inquiries
        </CardTitle>
        <CardDescription className="text-base mt-2">
          Whitelisting Inquiry for Aizalea Private Round
        </CardDescription>
      </CardHeader>
      
      <Form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          <FormField control={control} name="full_name" rules={{
          required: "Full Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <input type="text" className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30" placeholder="Jane Doe" {...field} value={field.value || ""} />
                </FormControl>
                <FormDescription>
                  Enter your legal name as it appears on official documents.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email Address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <input type="email" className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30" placeholder="jane@example.com" {...field} value={field.value || ""} />
                </FormControl>
                <FormDescription>
                  We'll use this to contact you about the private round.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="wallet_address" rules={{
          required: "Crypto Wallet Address is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Crypto Wallet Address</FormLabel>
                <FormControl>
                  <input type="text" className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 font-mono" placeholder="0xABC123... or your BSC/ETH address" {...field} value={field.value || ""} />
                </FormControl>
                <FormDescription>
                  Provide the address where you intend to receive Aizalea tokens.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="investment_amount" rules={{
            required: "Investment Amount is required",
            min: {
              value: 1000,
              message: "Minimum investment is $1,000"
            },
            max: {
              value: 1000000,
              message: "Maximum investment is $1,000,000"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Intended Investment Amount (USD)</FormLabel>
                  <FormControl>
                    <input type="number" className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30" placeholder="e.g., 50000" {...field} value={field.value || ""} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : "")} />
                  </FormControl>
                  <FormDescription>
                    Min $1,000 – Max $1,000,000.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="country" rules={{
            required: "Country of Residence is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Country of Residence</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10 w-full">
                        <SelectValue placeholder="Select your country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map((country, i) => <SelectItem key={country} value={country} data-index={i}>
                          {country}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="agree_terms" rules={{
          required: "You must confirm your accredited investor status to proceed."
        }} render={({
          field
        }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-input p-4 bg-muted/30">
                <FormControl>
                  <input type="checkbox" className="size-4 shrink-0 rounded-sm border border-primary shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 accent-primary mt-0.5 cursor-pointer" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-medium cursor-pointer">
                    I confirm that I am an accredited investor and agree to the private round terms.
                  </FormLabel>
                  <FormDescription>
                    You must be an accredited investor to participate.
                  </FormDescription>
                </div>
              </FormItem>} />

          {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive font-medium">{submitError}</p>
            </div>}
        </CardContent>
        
        <CardFooter className="pt-2 pb-8 px-6">
          <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base h-12">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-5" />
                Processing Inquiry...
              </> : 'Submit Whitelisting Inquiry'}
          </Button>
        </CardFooter>
      </Form>
    </Card>;
}
export default function WhitelistingInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="whitelisting-inquiry" className="w-full bg-background py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-ring/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 md:px-6 max-w-3xl relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <FormProvider formId="whitelisting_inquiry_private_round" sectionName="Whitelisting Inquiry" defaultValues={{
        full_name: "",
        email: "",
        wallet_address: "",
        investment_amount: "",
        country: "",
        agree_terms: false
      }} submitText="Submit Whitelisting Inquiry" successMessage="Thank you for your interest. Our institutional team will review your application and contact you shortly regarding the private round allocation." errorMessage="There was an error submitting your inquiry. Please check your information and try again.">
          <InquiryForm />
        </FormProvider>
      </div>
    </section>;
}